import { Fragment, Node as PMNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin, Selection, NodeSelection, TextSelection, Transaction } from '@tiptap/pm/state';
import { Editor, getHTMLFromFragment, mergeAttributes, Node, ReactNodeViewRenderer } from '@tiptap/react';

import { BlockNodeView } from '@/components/block-node-view/block-node-view';

import {
  isBlockNode,
  getBlockStorage,
  findParentOfType,
  generateBlockNode,
  getBlockNodeContent,
  updateBlockInstanceIds,
  normalizeBlockInstanceHTML,
} from '@/utils/editor-utils';
import { copyToClipboard } from '@/utils/helper-utils';

import type { Block } from '@flintmint/core-shared/rxdb';
const attributePrefix = 'block';

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    blockNode: {
      cleanupBlockStorage: () => ReturnType;
      insertBlock: (block: Block) => ReturnType;
      selectBlock: (id: string, instanceId: string) => ReturnType;
      copyBlock: (position: number, nodeSize: PMNode) => ReturnType;
      convertBlockToText: (instanceId: string, pos: number, content: Fragment) => ReturnType;
      replaceBlockWithNew: (
        instanceIdToReplace: string,
        position: number,
        block: Block,
        options?: ReplaceBlockWithNewOptions
      ) => ReturnType;
      positionCursorAfterBlockUpdate: (pos: number, from: number, node: PMNode, tr?: Transaction) => ReturnType;
    };
  }
  interface Storage {
    blockNode: BlockNodeStorage;
  }
}

export interface BlockNodeAttrs {
  id: string;
  name: string;
  instanceId: string;
}

export interface BlockNodeStorage {
  focusedBlockInstanceId: string | null;
  selectedBlockInstanceId: string | null;
  activeBlockId: string | null;
}

export interface ReplaceBlockWithNewOptions {
  preserveSelection?: boolean;
}

export const BlockNode = Node.create<BlockNodeAttrs, BlockNodeStorage>({
  atom: false,
  name: 'blockNode',
  group: 'docChild',
  allowGapCursor: true,
  // Can contain blocks, but cannot contain another blockInstance because blockInstance is NOT in group "block"
  content: 'block+',
  isolating: true,
  defining: true,
  selectable: true,
  draggable: true,

  addHtmlSchemaAwareness() {
    return {
      tag: 'block-instance',
      name: 'Block instance',
      description: [
        'A reusable Block Instance embedded in the document.',
        'It can contain other content like paragraphs, headings, etc.',
        'Use it to reference an existing Block Instance by id/name.',
        'data-block-instanceId is an internal unique identifier (uuid) which should be added if it is not present',
        'CRITICAL STRUCTURE RULE: <block-instance> MUST be a top-level node.',
        'It MUST appear only as a direct child of the document root.',
        'It MUST NOT be nested inside ANY other node (paragraph, list, table, blockquote, callout, columns, etc).',
        'It MUST NOT be a child of another <block-instance>.',
        'If you need multiple Block Instances, place them as separate top-level siblings.',
        'If you would otherwise nest it, instead split the document so <block-instance> is top-level.',
      ].join(' '),
      attributes: [
        {
          attr: 'data-block-id',
          description: 'The Block id (string). Used to reference which block this instance represents.',
        },
        {
          attr: 'data-block-name',
          description: 'Human-readable block name (string). Optional but recommended for readability.',
        },
        {
          attr: 'data-block-instanceId',
          description: 'Unique instance id (string).',
        },
      ],
    };
  },

  addStorage() {
    return {
      activeBlockId: null,
      focusedBlockInstanceId: null,
      selectedBlockInstanceId: null,
    };
  },

  addAttributes() {
    return {
      ...configureAttribute('id', attributePrefix),
      ...configureAttribute('name', attributePrefix),
      ...configureAttribute('instanceId', attributePrefix),
    };
  },

  parseHTML() {
    return [{ tag: 'block-instance' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['block-instance', mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(BlockNodeView);
  },

  addCommands() {
    return {
      insertBlock: (block: Block) => {
        return ({ state, dispatch }) => {
          const instanceId = crypto.randomUUID();
          const { schema, tr, selection } = state;

          const blockNode = generateBlockNode(block, schema, instanceId);

          tr.insert(selection.from - 1, blockNode);

          dispatch?.(tr.scrollIntoView());
          return true;
        };
      },

      selectBlock: (id, instanceId) => {
        return ({ state, dispatch, editor }) => {
          const { tr } = state;

          const storage = getBlockStorage(editor as Editor);

          storage.selectedBlockInstanceId = instanceId;
          storage.focusedBlockInstanceId = null;
          storage.activeBlockId = id;

          // Force ProseMirror to re-render node views by adding metadata tag to trigger view updates
          dispatch?.(tr.setMeta('blockNode:selectedBlockInstanceId', instanceId));

          return true;
        };
      },

      cleanupBlockStorage: () => {
        return ({ editor }) => {
          const storage = getBlockStorage(editor as Editor);

          storage.selectedBlockInstanceId = null;
          storage.focusedBlockInstanceId = null;
          storage.activeBlockId = null;

          editor.commands.focus();
          return true;
        };
      },

      convertBlockToText: (instanceId, pos, content) => {
        return ({ state, dispatch, editor }) => {
          const tr = state.tr.replaceWith(pos, pos + state.doc.nodeAt(pos)!.nodeSize, content);
          const storage = editor.storage.blockNode as BlockNodeStorage;
          if (storage.selectedBlockInstanceId === instanceId) {
            storage.selectedBlockInstanceId = null;
          }

          dispatch?.(tr);
          return true;
        };
      },

      replaceBlockWithNew: (instanceId, pos, block, options) => {
        return ({ state, dispatch, editor }) => {
          const {
            schema,
            selection: { from },
          } = state;

          const node = state.doc.nodeAt(pos);
          if (!node || !isBlockNode(node)) {
            return false;
          }

          const newBlockNode = generateBlockNode(block, schema, instanceId);
          const tr = state.tr.replaceWith(pos, pos + node.nodeSize, newBlockNode);

          if (options?.preserveSelection) {
            dispatch?.(tr);
            return true;
          }

          editor.commands.positionCursorAfterBlockUpdate(pos, from, node, tr);

          return true;
        };
      },

      positionCursorAfterBlockUpdate: (pos, from, node, externalTr) => {
        return ({ state, dispatch }) => {
          const tr = externalTr ?? state.tr;

          const mappedPos = tr.mapping.map(pos);
          const newNode = tr.doc.nodeAt(mappedPos);

          if (!newNode) {
            dispatch?.(tr);
            return true;
          }

          const start = mappedPos + 1;
          const end = mappedPos + newNode.nodeSize - 1;

          const clamp = (p: number) => Math.max(start, Math.min(end - 1, p));

          const wasAtEndOfOldNode = from === pos + (node?.content?.size ?? 0);
          const preferred = wasAtEndOfOldNode ? end - 1 : clamp(from);

          const tryFindInside = (p: number, dir: 1 | -1) => {
            const $p = tr.doc.resolve(clamp(p));
            const sel = Selection.findFrom($p, dir, true /* textOnly */);
            if (sel instanceof TextSelection) {
              const inside = sel.$from.pos >= start && sel.$from.pos <= end - 1;
              if (inside) {
                return sel;
              }
            }
            return null;
          };

          let sel: Selection | null = tryFindInside(preferred, -1) || tryFindInside(end - 1, -1) || tryFindInside(start, 1);

          if (!sel) {
            sel = NodeSelection.create(tr.doc, mappedPos);
          }

          tr.setSelection(sel);
          dispatch?.(tr);
          return true;
        };
      },

      copyBlock: (position, node) => {
        return ({ editor, state: { tr } }) => {
          const { blockText } = getBlockNodeContent(editor as Editor, node);

          const slice = tr.doc.slice(position, position + node.nodeSize);
          const html = getHTMLFromFragment(slice.content, editor.schema);

          copyToClipboard({ content: html, textContent: blockText });
          return true;
        };
      },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        state: {
          init: () => ({ focusedId: null }),
          apply(tr: Transaction, prev, _old, newState) {
            const force = !!tr.getMeta('block-focus')?.force;

            if (!tr.selectionSet && !force) {
              return prev;
            }

            const { selection } = newState;

            const parentBlock = findParentOfType(selection.$from, 'blockNode');
            const focusedInstanceId = parentBlock?.attrs?.instanceId ?? null;

            if (!force && focusedInstanceId === prev.focusedId) {
              return prev;
            }

            const storage = editor.storage.blockNode as BlockNodeStorage;
            storage.focusedBlockInstanceId = focusedInstanceId;

            if (force || !focusedInstanceId || focusedInstanceId !== storage.selectedBlockInstanceId) {
              storage.selectedBlockInstanceId = null;
            }

            return { focusedId: focusedInstanceId };
          },
        },
        props: {
          handleDOMEvents: {
            focus(view) {
              view.dispatch(view.state.tr.setMeta('block-focus', { force: true }));
              return false;
            },
          },
        },
      }),
      new Plugin({
        props: {
          decorations: (state) => {
            const decorations: Decoration[] = [];

            state.doc.descendants((node, pos) => {
              if (node.type.isBlock && node.childCount === 0) {
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    class: 'empty-node',
                  })
                );
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
      new Plugin({
        props: {
          handleKeyDown(view, event) {
            const { state, dispatch } = view;
            const { selection, doc } = state;

            const allContentSelected = selection.from <= 2 && selection.to === doc.content.size - 1;
            const isDelete = ['Backspace', 'Delete'].includes(event.key);

            if (allContentSelected && isDelete) {
              dispatch(state.tr.delete(0, doc.content.size));
              return true;
            }

            return false;
          },
          transformPastedHTML(html: string, view) {
            const { state } = view;
            const { from, to } = state.selection;

            let isInsideBlock = false;

            state.doc.nodesBetween(from, to, (node) => {
              if (node.type === state.schema.nodes.blockNode) {
                isInsideBlock = true;
                return false;
              }
              return true;
            });

            if (isInsideBlock) {
              // If pasting inside a block unwrap blocks into inline content
              return normalizeBlockInstanceHTML(html);
            }

            if (html.includes('block-instance')) {
              return updateBlockInstanceIds(html);
            }

            return html;
          },
        },
      }),
    ];
  },
});

export const configureAttribute = (name: string, prefix: string = '', value: any = null) => {
  const attribute = ['data', prefix, name].filter(Boolean).join('-');
  return {
    [name]: {
      default: value,
      parseHTML: (el: HTMLElement) => el.getAttribute(attribute),
      renderHTML: (attrs: Record<string, any>) => {
        return { [attribute]: attrs[name] };
      },
    },
  };
};
